<template>
  <component
    :is="designMode ? 'draggable' : 'div'"
    v-model="mutableFormRows"
    v-bind="designMode ? 
      {
        ...dragOptions,
        group: { name: 'form-design' },
        handle: '.form-element-design-action-drag-handle',
      } : {}
    "
    class="fts-form-container"
    :class="{'fts-form-container--editable': drag}"
    ref="ftsForm"
    @change="updateRow"
    @start="drag=true"
    @end="drag=false"
  >
    <div v-for="(row, rowIndex) in mutableFormRows" :key="rowIndex" class="form-row">
      <component
        :is="designMode ? 'draggable' : 'div'"
        v-model="row.items"
        v-bind="
          designMode ? {
            ...dragOptions,
            group: { name: 'form-design'},
            fallbackTolerance: 5,
            handle: '.form-element-design-action-drag-handle'
          } : {}
        "
        class="fts-form-wrapper"
        @change="updateColumn"
        @start="drag=true"
        @end="drag=false"
      >
        <template v-for="item in row.items">   
          <!-- section -->
          <template v-if="item && item.input === 'section'">
            <div 
              class="fts-form-section" 
              :class="{
                'form-section-hoverable fts-form-group': item.type !== 'column',
                'form-section-collapse': item.type === 'collapse',
                'form-section-tab': item.type === 'tab',
                'form-section--expanded': item.type === 'collapse' && item.expanded,
                'comfort-design': comfortDesign,
              }"
              :key="item.id" 
              v-if="sectionVisibility(item)"
            >
              <div class="form-section-header" @click="collapseExpand(item)">
                <div class="form-section-header-wrapper">
                  <!-- tab title -->
                  <template v-if="item.type === 'tab'">
                    <template v-for="tab in item.tabs">
                      <fts-form-tab-header 
                        :tab="tab" 
                        :validator="validator" 
                        :key="tab.id" 
                        @tab:click="selectTab(item, tab)" 
                        v-if="sectionVisibility(tab)"
                      ></fts-form-tab-header>
                    </template>
                  </template>
                  <!-- section title -->
                  <div class="form-section-header-title" v-else-if="item.type !== 'column'">{{item.header? item.header : '(Untitled)'}}</div>
                </div>
                <!-- section collapse toggle -->
                <div class="form-section-header-action" v-if="item.type == 'collapse'">
                  <font-awesome-icon :icon="['fas', 'caret-right']" />
                </div>
              </div>
              <!-- section content -->
              <fts-transition-expand>
                <!-- class:fts-scrollbar - temp fix signature overflow issue -->
                <fts-form
                  :form="retrieveTabForm(item)"
                  :form-data="formData"
                  :validator="validator"
                  :vector-id="vectorId"
                  :activity-id="activityId"
                  :view="view"
                  :share-form="shareForm"
                  :comfort-design="comfortDesign"
                  :form-settings="formSettings"
                  :form-element-overflow-auto="formElementOverflowAuto"
                  :form-element-disabled="formElementDisabled"
                  :class="{'fts-scrollbar': item.type !== 'column'}"
                  @eventTriggered="triggerEvent"
                  v-if="item.expanded || item.type !== 'collapse'"
                />
              </fts-transition-expand>
            </div>
          </template>
          <!-- form element -->
          <fts-form-element
            :key="item.id"
            :item="item"
            :vector-id="vectorId"
            :activity-id="activityId"
            :validator="validator"
            :form-data="formData"
            :view="view"
            :share-form="shareForm"
            :context="context"
            :username="username"
            :comfort-design="comfortDesign"
            maintain-label-height
            :overflow-auto="formElementOverflowAuto"
            :design-mode="designMode"
            :form-settings="formSettings"
            :disabled="formElementDisabled"
            :class="{
              'form-element-design': designMode,
              'form-element--teammate-filled':
                teammateFilledFields[item.fieldName] || teammateFilledFields[item.displayName],
            }"
            @design:change="$emit('design:change')"
            @design:column="evt => addColumnToField(evt, item)"
            @eventTriggered="triggerEvent"
            v-else-if="show(item)"
          >
            <div slot="header" class="form-element-design-action-container" v-if="designMode">
              <fts-form-design-shortcut
                :modules="modules"
                :options="shortcutOptions"
                :selected-options="shortcutSelectedOptions"
                :elements="shortcutElements"
                :exclude-types="designExcludeTypes"
                suggestion
                placement="bottom-start"
                :disable-create="view"
                class="form-element-design-action"
                @option:selected="evt => addFieldToRow(evt, rowIndex + 1)"
                @option:create="evt => createInformation(evt, rowIndex + 1)"
              >
                <fts-tippy>
                  <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                  <div slot="content" class="tippy-hint">
                    <span class="tippy-hint-highlight">Click</span> to add a field below
                  </div>
                </fts-tippy>
              </fts-form-design-shortcut>
              <fts-dropdown-select
                append-to-body
                :offset="[0, 0]"
                prevent-overflow-alt-axis
                prevent-overflow-tether-off
              >
                <fts-tippy slot="toggle" class="form-element-design-action">
                  <font-awesome-icon
                    :icon="['fas', 'grip-vertical']"
                    class="form-element-design-action-drag-handle"
                    fixed-width
                  />
                  <template #content>
                    <div class="tippy-hint">
                      <span class="tippy-hint-highlight">Drag</span> to move
                    </div>
                    <div class="tippy-hint">
                      <span class="tippy-hint-highlight">Click</span> to open menu
                    </div>
                  </template>
                </fts-tippy>
                <template #menu="{ select }">
                  <div class="dropdown-option" @click="removeColumn(item, row.items);select();">
                    <div class="dropdown-option-icon">
                      <font-awesome-icon :icon="['fas', 'xmark']" fixed-width />
                    </div>
                    <div>Remove field</div>
                  </div>
                  <template v-if="['section', 'empty', 'description', 'image', 'button', 'custom'].indexOf(item.input) == -1">
                    <div class="dropdown-divider"></div>
                    <div class="dropdown-option" @click="openInformationDesignModal(item, row.items);select();">
                      <div class="dropdown-option-icon">
                        <font-awesome-icon :icon="['fas', 'edit']" fixed-width />
                      </div>
                      <div>Edit information</div>
                    </div>
                    <div class="dropdown-option" @click="openInformationDeleteConfirmation(item, row.items);select();">
                      <div class="dropdown-option-icon">
                        <font-awesome-icon :icon="['far', 'trash-can']" fixed-width />
                      </div>
                      <div>Delete information</div>
                    </div>
                  </template>
                </template>
              </fts-dropdown-select>
            </div>
          </fts-form-element>
        </template>
      </component>
    </div>
    <div slot="footer" role="group" key="footer" v-if="designMode">
      <template v-if="!view">
        <div class="form-hint-group" v-if="withoutInformation">
          <div class="form-hint" v-if="activityId === 'create'">
            Start by adding a field to your form.
          </div>
          <div class="form-hint" v-else>
            Select existing or adding new fields to update information. Leave empty if none.
          </div>
        </div>

        <fts-form-design-shortcut
          :modules="modules"
          :options="shortcutOptions"
          :selected-options="shortcutSelectedOptions"
          :elements="shortcutElements"
          :exclude-types="designExcludeTypes"
          suggestion
          placement="bottom-start"
          class="form-hint-group form-hint-action-wrapper"
          @option:selected="evt => addFieldToRow(evt)"
          @option:create="evt => createInformation(evt)"
        >
          <div class="form-hint-action">
            <font-awesome-icon :icon="['fas', 'plus']" fixed-width class="form-hint-action-icon"/>Add a field
          </div>
        </fts-form-design-shortcut>
      </template>

      <fts-information-design-modal client-mode ref="ftsInformationDesignModal" />
      <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
    </div>
  </component>
</template>

<script>
import draggable from 'vuedraggable';

import commons from '../commons/commons';
import canvasDesign from '../commons/canvasDesign';
import formDesign from '../commons/formDesign';
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore} from '../store/canvasDesignStore';

import FTSDropdownSelect from './FTSDropdownSelect';
import FTSFormDesignShortcut from '../design/FTSFormDesignShortcut';
import FTSFormElement from './FTSFormElement';
import FTSFormTabHeader from './form/FTSFormTabHeader';
import FTSInformationDesignModal from '../design/FTSInformationDesignModal';
import FTSInformationDeleteConfirmation from '../design/FTSInformationDeleteConfirmation';
import FTSTippy from './FTSTippy';
import FTSTransitionExpand from './FTSTransitionExpand';

export default {
  components: {
    draggable,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-form-design-shortcut": FTSFormDesignShortcut,
    "fts-form-element": FTSFormElement,
    "fts-form-tab-header": FTSFormTabHeader,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-information-delete-confirmation": FTSInformationDeleteConfirmation,
    "fts-tippy": FTSTippy,
    "fts-transition-expand": FTSTransitionExpand
  },
  props: {
    vectorId: [String, Number],
    activityId: String,
    form: Object,
    formData: Object,
    validator: Object,
    context: Object,
    view: Boolean,
    shareForm: Boolean,
    username: String,
    comfortDesign: Boolean,
    formSettings: Object,
    formElementOverflowAuto: Boolean,
    formElementDisabled: Boolean,
    designMode: Boolean,
    shortcutOptions: Array,
    shortcutSelectedOptions: Array,
    shortcutElements: Array,
    withoutInformation: Boolean
  },
  data: () => ({
    enabled: true, /* no reference, to-do remove */
    modal: true, /* no reference, to-do remove */
    dragOptions: commons.dragOptions,
    drag: false
  }),
  methods: {
    triggerEvent: function(event) {
      this.$emit('eventTriggered', event);
    },
    selectTab: function(item, tab) {
      item.tabs.forEach(x => this.$set(x, 'selected', false));
      this.$set(tab, 'selected', true);
    },
    retrieveTabForm: function(item) {
      if(item.type == 'tab') {
        let tabSelected = item.tabs.find(x => x.selected);
        if(tabSelected == null)
          tabSelected = item.tabs[0];
        return tabSelected.design;
      }

      return item.design;
    },
    collapseExpand: function(item) {
      if(item.type === 'collapse') {
        if (!item.expanded)
          this.$set(item, "expanded", false);

        item.expanded = !item.expanded;
      }
    },
    sectionVisibility (item) {
      let vm = this

      function findElement(elements) {
        return elements.some(x => {
          if (x.tabs)
            return findElement(x.tabs)
          if (x.design && x.design.rows)
            return findElement(x.design.rows)
          if (x.items) 
            return findElement(x.items)
          
          return vm.show(x) && x.input != 'empty'
        })
      }

      if (item.visible === false)
        return false;
      else if (item.type === 'tab' && item.tabs)
        return findElement(item.tabs);
      else if (item.design && item.design.rows)
        return findElement(item.design.rows);
    },
    show(item) {
      return commons.formElementVisibility(item, this.formData);
    },
    /* design mode */
    clearEmptyRow() {
      let rows = this.form.rows.slice();
      rows.forEach((row) => {
        if (!row.items || row.items.length === 0) {
          const index = this.form.rows.indexOf(row);
          this.form.rows.splice(index, 1);
        }
      });
    },
    updateRow(evt) {
      if (this.designMode) {
        if (evt.added && evt.added.element) {
          if (evt.added.newIndex === -1) {
            this.form.rows.splice(0, 0, {
              index: commons.newUUID(),
              items: [evt.added.element]
            })
          } else {
            const index = this.form.rows.indexOf(evt.added.element);
            this.form.rows.splice(index, 1, {
              index: commons.newUUID(),
              items: [evt.added.element]
            });
          }
          this.clearEmptyRow();
        }
        this.$emit('design:change');
      }
    },
    updateColumn(evt) {
      if (this.designMode) {
        /* SS06064: a field dragged OUT of this row (into a section, or another row) arrives
           here as `removed` only — the receiving list gets the `added` — so the row it left
           behind stayed in the schema with an empty items[] and rendered as a stray divider. */
        if ((evt.added && evt.added.element) || evt.removed) {
          this.clearEmptyRow();
        }
        this.$emit('design:change');
      }
    },
    removeColumn(item, items) {
      const index = items.indexOf(item);
      items.splice(index, 1);
      this.clearEmptyRow();
      this.$emit('design:change');
    },
    addFieldToRow(item, index) {
      const rowIndex = index ? index : this.form.rows.length;
      if (formDesign.isElement(item.input) && !item.id) {
        item.id = commons.newUUID();
      }
      if (item) {
        this.form.rows.splice(rowIndex, 0, {
          index: commons.newUUID(),
          items: [item]
        });
        this.clearEmptyRow();
      }
      this.$emit('design:change');
    },
    async createInformation(item, index) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      let res = canvasDesign.initInformation(item);
      if (
        !res.displayName || 
        [7, 8, 14, 15, 20, 21, 27, 28].indexOf(res.type) > -1 ||
        res.type === null ||
        res.type === undefined
      ) {
        res = await this.$refs.ftsInformationDesignModal.show(res, null, null, { excludeTypes: this.designExcludeTypes });
      }
      if (res && res !== true) {
        try {
          canvasDesign.setInformation(canvas, res);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          this.addFieldToRow(res, index);
        } catch(err) {
          console.error(err);
          this.$toasted.global.error();
        }
      }
    },
    deleteInformation(canvas, item, rowItems) {
      canvasDesign.deleteInformation(canvas, item.id);
      canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      this.removeColumn(item, rowItems);
    },
    async openInformationDeleteConfirmation(item, rowItems) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const res = await this.$refs.ftsInformationDeleteConfirmation.show({
        canvas: canvas,
        information: item,
        payload: { excluded: [this.activityId]}
      });
      if (res === true) {
        this.deleteInformation(canvas, item, rowItems);
      }
    },
    async openInformationDesignModal(item, rowItems) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const information = canvasDesign.getInformation(canvas, item.id);
      if (information) {
        const res = await this.$refs.ftsInformationDesignModal.show(
          {...canvasDesign.initInformation(information)},
          null, 
          null, 
          { activityId: this.activityId, excludeTypes: this.designExcludeTypes }
        );
        if (res && res !== true) {
          const rowItemIndex = rowItems.indexOf(item);
          if (rowItemIndex > -1) {
            this.$set(rowItems, rowItemIndex, {...item, ...res});
          }
          canvasDesign.setInformation(canvas, res);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          commons.expandWorkDesignInternal(this.canvasInformation, item);
          this.$emit('design:change');
        } else if (res === true) {
          this.deleteInformation(canvas, information, rowItems);
        }
      } else {
        this.$toasted.global.error(`Something went wrong. Information #${item.displayName} not found.`);
      }
    },
    async addColumnToField(item, parent) {
      if (parent && parent.id) {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let res = canvasDesign.initInformation(item);
        if (
          !res.displayName || 
          [7, 8, 20, 21, 27, 28].indexOf(res.type) > -1 ||
          res.type === null ||
          res.type === undefined
        ) {
          res = await this.$refs.ftsInformationDesignModal.show(
            canvasDesign.initInformation(item),
            null,
            null,
            { parentId: parent.id, excludeTypes: this.designExcludeTypes }
          );
        }
        if (res && res !== true) {
          canvasDesign.setInformation(canvas, res, parent.id);
          formDesign.addInformationFieldsToForms(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          commons.expandWorkDesignInternal(this.canvasInformation, parent);
          this.$emit('design:change');
        }
      }
    }
  },
  computed: {
    canvasInformation() {
      return canvasDesignStore.getters.canvasInformation;
    },
    modules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    mutableFormRows: {
      get() {
        if (this.designMode) {
          return this.form && this.form.rows;
        } else {
          try {
            return this.form && this.form.rows.filter((x) => x.items && x.items.some((y) => this.show(y)));
          } catch(err) {
            console.error(err);
          }
          return [];
        }
      },
      set(val) {
        this.form.rows = val;
      }
    },
    designExcludeTypes() {
      let types = [15];
      !authorizationStore.getters.isBeta && (types = types.concat(9));
      return types;
    },
    /* §4.3.1 live-form GNey fill: fields the last completed fill wrote, for the per-field
       attribution tint. Guarded — FTSForm also renders under stores without teammateFill
       (designer/preview contexts), where this is simply empty. */
    teammateFilledFields() {
      const fill = this.$store && this.$store.state && this.$store.state.teammateFill;
      if (!fill || fill.status !== "done" || !Array.isArray(fill.filled)) return {};
      const set = {};
      fill.filled.forEach((key) => { set[key] = true; });
      return set;
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-form-container {
  .fts-form-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    margin-left: -0.75rem;
    margin-right: -0.75rem;
    .fts-form-section {
      margin-left: 0.75rem;
      margin-right: 0.75rem;
    }
    .fts-form-element {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    /* §4.3.1: subtle attribution tint on GNey-filled fields, until undo/dismiss/submit */
    .fts-form-element.form-element--teammate-filled {
      background-color: rgba(var(--fts-primary-rgb), 0.06);
      border-radius: 0.375rem;
      transition: background-color 0.4s ease;
    }
  }
  /* form section */
  .fts-form-section {
    min-width: 400px;
    width: auto;
    flex: 1 1 400px;
    &.form-section-hoverable {
      border: 1px solid var(--fts-form-control-border);
      .form-section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 1rem;
        border-radius: inherit;
        & + .fts-form-container {
          padding-top: 1rem;
          border-top: 1px solid var(--fts-form-control-border);
          > .form-row {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }
        .form-section-header-wrapper {
          flex: 1;
          display: flex;
          .form-section-header-title {
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
            font-weight: 500;
            font-size: $font-size;
            line-height: $line-height;
            color: var(--fts-form-label);
          }
        }
        .form-section-header-action {
          margin-left: 1rem;
          color: var(--fts-default);
          svg {
            transition: transform 0.3s;
            font-size: 0.875rem;
          }
        }
      }
    }
    &.form-section-collapse {
      .form-section-header {
        cursor: pointer;
      }
    }
    &.form-section-tab {
      .form-section-header{
        .form-section-header-wrapper {
          flex-wrap: wrap;
          .form-section-header-title {
            position: relative;
            padding-left: 1rem;
            padding-right: 1rem;
            &:hover {
              cursor: pointer;
              &:not(.form-section-tab-selected) {
                background-color: var(--fts-gray-100);
              }
            }
            &.form-section-tab-selected {
              color: var(--fts-primary);
              &:hover {
                @include theme() {
                  background-color: rgba(theme-get('fts-primary'), 0.095);
                }
              }
            }
          }
          .form-section-header-error-icon {
            @include absolute-center-y;
            right: 0.5rem;
            color: var(--fts-danger);
          }
        }
      }
    }
    &.form-section--expanded {
      .form-section-header {
        .form-section-header-action {
          svg {
            transform: rotate(90deg);
          }
        }
      }
    }
    &.comfort-design {
      &.form-section-hoverable {
        border-radius: $comfort-border-radius;
        .form-section-header {
          .form-section-header-wrapper {
            .form-section-header-title {
              font-size: $comfort-font-size;
              line-height: $comfort-line-height;
            }
          }
        }
      }
    }
  }
  &.fts-form-container--editable {
    .form-row:hover {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
  }
  .form-element-design {
    position: relative;
    &:hover {
      .form-element-design-action-container {
        display: flex;
      }
    }
  }
  .form-element-design-action-container {
    position: absolute;
    top: calc(0.375rem + 1px);
    left: 1rem;
    display: none;
    align-items: center;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    border-radius: 0.25rem;
    box-shadow: var(--fts-elevation-1);
    background-color: var(--fts-popover-alt-background);
    z-index: 1;
  }
  .form-element-design-action {
    font-size: 0.75rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
  .form-element-design-action-drag-handle {
    cursor: grab;
  }
}
</style>
<style lang="scss">
.fts-form-container {
  .form-element-design-action-container:hover + .fts-form-group > .fts-form-label,
  .form-element-design-action-container + .fts-form-group > .fts-form-label:hover {
    background-color: rgba(var(--fts-gray-400-rgb), 0.085);
  }
}
</style>